import { Circle, Clear, EditNote } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { botDataDefault } from "../../../../../../../utils/globalBotData";
import { nodeInputStyles } from "../../GeneralNodeStyles";
import Checkbox from "../../../../../../../Components/CheckBox/Checkbox";
import { LLAMA_PARSE_PROMPT } from "../../../../../../../utils/defaultPrompts";
import { getDataset } from "../../../../../../../utils/IndexedDBDexieUtility";
import { dialogStyles } from "../../../../../../../Constants/dialogStyles";
import { accordionStyles } from "../../../../../../../Constants/accordionStyles";
import theme, {
  createLLMTypography,
  scrollbarStyles,
} from "../../../../../../../Constants/theme";
import {
  primaryButtonStyles,
  tertiaryButtonStyles,
} from "../../../../../../../Constants/buttonStyles";
import {
  inputStyles,
  radioGroupStyles,
} from "../../../../../../../Constants/inputStyles";

const DocumentParser = memo(
  ({ data, parser, setParser, parsingInstructions }) => {
    const { botData, setBotData, nodeId } = data;
    const [datasets, setDatasets] = useState(botData?.datasets || []);
    const organization = useSelector((state) => state.organization);

    const [open_LP_PromptDialog, setOpen_LP_PromptDialog] = useState(false);
    const [open_RCTS_PromptDialog, setOpen_RCTS_PromptDialog] = useState(false);
    const originalStoredPrompt =
      parsingInstructions?.llama_parse_prompt || LLAMA_PARSE_PROMPT;
    const originalModeObj =
      parsingInstructions?.llama_parse_mode || getObjFromMode("auto");
    const [llamaParseModeStr, setLlamaParseModeStr] = useState(
      getModeFromObj(originalModeObj)
    );
    const [openUnstructuredIODialog, setOpenUnstructuredIODialog] =
      useState(false);
    const originalUnstructuredIOMode =
      parsingInstructions?.unstructuredioInstructions?.unstructuredio_mode ||
      botDataDefault.parsing_instructions.unstructuredio_mode;
    const [unstructuredIOMode, setUnstructuredIOMode] = useState(
      originalUnstructuredIOMode
    );
    const [originalUnstructuredIOModeState] = useState(
      originalUnstructuredIOMode
    );

    // Keep around the original mode string, if you need it
    const [originalModeStr] = useState(getModeFromObj(originalModeObj));
    // Current state for Llama Parse Prompt
    const [llamaParsePrompt, setLlamaParsePrompt] =
      useState(originalStoredPrompt);
    const [originalPrompt, setOriginalPrompt] = useState(originalStoredPrompt);

    const [parsing_instructions, setParsing_instructions] = useState(
      botData?.parsing_instructions || {}
    );
    const [global_parsing_instructions, set_global_parsing_instructions] =
      useState({
        bounding_box: "0,0,0,0",
        exclude_title_page: false,
        mode: "auto",
        title_page_number: "1",
      });
    const [selectedModel, setSelectedModel] = useState(
      botData?.parsing_instructions?.third_party_multimodal_llm?.model || ""
    );

    // Store refs for the newly added splitter
    const splitterRefs = useRef({});
    const [splitters, setSplitters] = useState(
      parsing_instructions?.splitters || {}
    );
    useEffect(() => {
      const __PARSING_INSTRUCTIONS__ = JSON.parse(
        localStorage.getItem(`parsing_instructions_${nodeId}`)
      );
      if (__PARSING_INSTRUCTIONS__) {
        setParsing_instructions(__PARSING_INSTRUCTIONS__);
        setSplitters(__PARSING_INSTRUCTIONS__.splitters);
      }
      const loadData = async () => {
        const storedDatasets = await getDataset(`datasets_${nodeId}`);

        if (storedDatasets) {
          setDatasets(JSON.parse(storedDatasets));
        }
      };

      loadData();
    }, []);

    const updateParsingInstructions = (fieldsToUpdate) => {
      setBotData((prev) => {
        const merged = {
          ...prev.parsing_instructions,
          ...fieldsToUpdate,
        };

        // Deep-merge per-file entries if needed
        for (const key in fieldsToUpdate) {
          if (
            typeof fieldsToUpdate[key] === "object" &&
            !Array.isArray(fieldsToUpdate[key]) &&
            key in prev.parsing_instructions
          ) {
            merged[key] = {
              ...prev.parsing_instructions[key],
              ...fieldsToUpdate[key],
            };
          }
        }

        setParsing_instructions(merged);
        localStorage.setItem(
          `parsing_instructions_${nodeId}`,
          JSON.stringify(merged)
        );

        return {
          ...prev,
          parsing_instructions: merged,
        };
      });
    };

    const handleInputChangeSplitters = (type, index, value) => {
      const updatedSplitters = { ...splitters };
      updatedSplitters[type][index] = value;
      setBotData((prevData) => ({
        ...prevData,
        parsing_instructions: {
          ...prevData.parsing_instructions,
          splitters: updatedSplitters,
        },
      }));
      localStorage.setItem(
        `parsing_instructions_${nodeId}`,
        JSON.stringify({
          ...parsing_instructions,
          splitters: updatedSplitters,
        })
      );
      setSplitters(updatedSplitters);
    };

    const handleAddSplitter = (type) => {
      setSplitters((prevSplitters) => {
        const newSplitters = { ...prevSplitters };

        if (!newSplitters[type]) {
          newSplitters[type] = [];
        }

        newSplitters[type].push("");

        setTimeout(() => {
          const newIndex = newSplitters[type].length - 1;
          const inputRef = splitterRefs.current[`${type}-${newIndex}`];
          if (inputRef) {
            inputRef.scrollIntoView({ behavior: "smooth", block: "start" });
            inputRef.focus();
          }
        }, 100);

        setSplitters(newSplitters);
        updateParsingInstructions({ splitters: newSplitters });

        return newSplitters;
      });
    };

    const handleRemoveSplitter = (type, index) => {
      const updatedList = splitters[type]?.filter((_, i) => i !== index) || [];

      const updatedSplitters = {
        ...splitters,
        [type]: updatedList,
      };

      setSplitters(updatedSplitters);
      updateParsingInstructions({ splitters: updatedSplitters });
    };

    // const handleInputChangeSplitters = (type, value) => {
    //   setSplitters({
    //     ...splitters,
    //     [type]: value.split(",").map((item) => item.trim()), // Convert string to list
    //   });

    //   setBotData((prevData) => ({
    //     ...prevData,
    //     parsing_instructions: {
    //       ...prevData.parsing_instructions,
    //       splitters: {
    //         ...prevData.parsing_instructions.splitters,
    //         [type]: value.split(",").map((item) => item.trim()), // Convert string to list
    //       },
    //     },
    //   }));

    // };

    const handleApplyForAll = () => {
      // merge global into each file's instructions
      const newInstructions = { ...parsing_instructions };

      datasets.forEach(({ fileName }) => {
        newInstructions[fileName] = {
          ...(newInstructions[fileName] || {}),
          ...global_parsing_instructions,
        };
      });

      setParsing_instructions(newInstructions);
      // setBotData((prev) => ({
      //   ...prev,
      //   parsing_instructions: {
      //     ...newInstructions,
      //     default_instructions: { ...global_parsing_instructions },
      //   },
      // }));
      // localStorage.setItem(
      //   `parsing_instructions_${nodeId}`,
      //   JSON.stringify(newInstructions)
      // );

      // Also store the global defaults as `default_instructions`
      newInstructions.default_instructions = { ...global_parsing_instructions };

      // Update everything in one go
      updateParsingInstructions(newInstructions);

      data.showSnackbar("Parsing instructions applied to all files", "success");
    };

    const handleInputChange = (fileName, field, value) => {
      if (fileName === "All") {
        //for UI of global inputs only
        const updatedGlobal = {
          ...global_parsing_instructions,
          [field]: value,
        };
        set_global_parsing_instructions(updatedGlobal);

        // Save as default_instructions
        updateParsingInstructions({
          default_instructions: updatedGlobal,
        });
      } else {
        // // only update the single file
        // newInstructions[fileName] = {
        //   ...(newInstructions[fileName] || {}),
        //   [field]: value,
        // };
        // // commit to state
        // setParsing_instructions(newInstructions);
        // setBotData((prev) => ({
        //   ...prev,
        //   parsing_instructions: newInstructions,
        // }));
        // // persist
        // localStorage.setItem(
        //   `parsing_instructions_${nodeId}`,
        //   JSON.stringify(newInstructions)
        // );
      }

      const updatedFile = {
        ...(parsing_instructions[fileName] || {}),
        [field]: value,
      };

      updateParsingInstructions({
        [fileName]: updatedFile,
      });
    };

    const handleChangeModelLLM = (event, fileName) => {
      const value = event.target.value;
      if (fileName === "All") {
        const updatedGlobal = {
          ...global_parsing_instructions,
          llm: value,
        };
        set_global_parsing_instructions(updatedGlobal);
        updateParsingInstructions({
          default_instructions: updatedGlobal,
        });
      } else {
        setSelectedModel(value);
        setParsing_instructions((prevData) => ({
          ...prevData,
          [fileName]: {
            ...prevData[fileName],
            llm: value,
          },
        }));
        // setBotData((prevData) => ({
        //   ...prevData,
        //   parsing_instructions: {
        //     ...prevData.parsing_instructions,
        //     [fileName]: {
        //       ...prevData.parsing_instructions[fileName],
        //       llm: value,
        //     },
        //   },
        // }));

        // localStorage.setItem(
        //   `parsing_instructions_${nodeId}`,
        //   JSON.stringify({
        //     ...parsing_instructions,
        //     [fileName]: { ...parsing_instructions[fileName], llm: value },
        //   })
        // );

        const updatedFileInstructions = {
          ...(parsing_instructions[fileName] || {}),
          llm: value,
        };
        updateParsingInstructions({
          [fileName]: updatedFileInstructions,
        });
      }
    };

    const vendorOptions = [
      { label: "gemini-2.0-flash-001", cost: "" },
      { label: "gemini-1.5-pro", cost: "" },
      { label: "openai-gpt-4o-mini", cost: "" },
      { label: "openai-gpt4o", cost: "" },
      { label: "anthropic-sonnet-3.5", cost: "" },
      { label: "anthropic-sonnet-3.7", cost: "" },
    ];

    function getModeFromObj(modeObj = {}) {
      if (modeObj.accurate) return "accurate";
      if (modeObj.premium) return "premium";
      if (modeObj.continuous) return "continuous";
      // default to "auto"
      return "auto";
    }

    function getObjFromMode(modeStr = "auto") {
      return {
        auto: modeStr === "auto",
        premium: modeStr === "premium",
        continuous: modeStr === "continuous",
        accurate: modeStr === "accurate",
      };
    }

    const handleParserChange = (event, type) => {
      const doc_parser = { ...parser };
      doc_parser[type] = event.target.checked;

      if (!Object.values(doc_parser).some((value) => value)) {
        doc_parser["recursive_character_text_splitter"] = true;
      }
      setParser({ ...doc_parser });
      localStorage.setItem(`parser_${nodeId}`, JSON.stringify(doc_parser));

      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            parser: doc_parser,
          },
        },
      }));
    };

    const handleUnstructuredIOModeChange = (event) => {
      const { value } = event.target;
      const updatedMode = {
        fast: false,
        hi_res: false,
        auto: false,
        ocr_only: false,
      };
      updatedMode[value] = true;
      setUnstructuredIOMode(updatedMode);
    };

    const handleSaveUnstructuredIODialog = () => {
      // Update the botData state
      // setBotData((prevData) => ({
      //   ...prevData,
      //   parsing_instructions: {
      //     ...prevData.parsing_instructions,
      //     unstructuredio_mode: unstructuredIOMode,
      //   },
      // }));

      // Update localStorage to reflect the entire parsing_instructions object
      // const updatedParsingInstructions = {
      //   ...botData.parsing_instructions,
      //   unstructuredio_mode: unstructuredIOMode,
      // };

      // localStorage.setItem(
      //   `parsing_instructions_${nodeId}`,
      //   JSON.stringify(updatedParsingInstructions)
      // );

      updateParsingInstructions({
        unstructuredio_mode: unstructuredIOMode,
      });

      // Close the dialog
      handleCloseUnstructuredIODialog();
    };

    const handleResetUnstructuredIODialog = () => {
      setUnstructuredIOMode(originalUnstructuredIOModeState);
    };

    const handleOpenUnstructuredIODialog = () => {
      setOpenUnstructuredIODialog(true);
    };

    const handleCloseUnstructuredIODialog = () => {
      setOpenUnstructuredIODialog(false);
    };

    const handleOpen_LP_Dialog = () => {
      setOpen_LP_PromptDialog(true);
    };
    const handleOpen_RCTS_Dialog = () => {
      setOpen_RCTS_PromptDialog(true);
    };

    const handleClose_LP_Dialog = () => {
      setOpen_LP_PromptDialog(false);
    };

    const handleClose_RCTS_Dialog = () => {
      cleanSplitters();
      setOpen_RCTS_PromptDialog(false);
    };

    const cleanSplitters = () => {
      setBotData((prevData) => {
        const updatedSplitters = {};

        let containsEmptyString = false;

        // Iterate over each splitter type
        Object.keys(prevData.parsing_instructions.splitters).forEach((type) => {
          // Remove duplicates using a Set and check for empty strings
          const uniqueSplitters = [
            ...new Set(prevData.parsing_instructions.splitters[type]),
          ];

          if (uniqueSplitters.includes("")) {
            containsEmptyString = true;
          }

          updatedSplitters[type] = uniqueSplitters;
        });

        if (containsEmptyString) {
          alert(
            "We have found empty string in the splitters. Passing an empty string will cause chunking in the middle of words."
          );
        }
        const _UPDATED_PARSINGS_ = {
          ...parsing_instructions,
          splitters: updatedSplitters,
        };
        localStorage.setItem(
          `parsing_instructions_${nodeId}`,
          _UPDATED_PARSINGS_
        );

        setSplitters(updatedSplitters);
        setParsing_instructions((prevData) => ({
          ...prevData,
          splitters: updatedSplitters,
        }));
        return {
          ...prevData,
          parsing_instructions: {
            ...prevData.parsing_instructions,
            splitters: updatedSplitters,
          },
        };
      });
    };

    const handleLlamaParseModeChange = (event) => {
      setLlamaParseModeStr(event.target.value);
    };

    const handleSave_LP_Dialog = () => {
      let updatedPrompt = llamaParsePrompt;

      if (!updatedPrompt.startsWith(`"""`)) {
        updatedPrompt = `"""` + updatedPrompt;
      }
      if (!updatedPrompt.endsWith(`"""`)) {
        updatedPrompt += `"""`;
      }
      const updatedModeObj = getObjFromMode(llamaParseModeStr);

      updateParsingInstructions({
        llama_parse_prompt: updatedPrompt,
        llama_parse_mode: updatedModeObj,
        third_party_multimodal_llm: {
          llm: selectedModel,
        },
      });

      // localStorage.setItem(
      //   `parsing_instructions_${nodeId}`,
      //   JSON.stringify({
      //     ...parsing_instructions,
      //     llama_parse_prompt: updatedPrompt,
      //     third_party_multimodal_llm: {
      //       llm: selectedModel,
      //     },
      //   })
      // );
      // setBotData((prevData) => ({
      //   ...prevData,
      //   parsing_instructions: {
      //     ...parsing_instructions,
      //     llama_parse_prompt: updatedPrompt,
      //     llama_parse_mode: updatedModeObj,
      //     third_party_multimodal_llm: {
      //       llm: selectedModel,
      //     },
      //   },
      // }));
      setLlamaParsePrompt(updatedPrompt);
      setOriginalPrompt(updatedPrompt);

      handleClose_LP_Dialog();
    };

    const handleReset_LP_Dialog = () => {
      setLlamaParsePrompt(LLAMA_PARSE_PROMPT);
    };

    const ioModes = [
      {
        value: "fast",
        label: "Basic",
        description:
          "Basic Strategy: $2/1000 pages. For simple, text-only documents.",
      },
      {
        value: "hi_res",
        label: "Advanced",
        description:
          "Advanced Strategy: $20/1000 pages. For PDFs, images, and complex file types.",
      },
      {
        value: "auto",
        label: "Auto",
        description:
          "Auto Mode: Switches between strategies based on content. By default, it uses the Basic Strategy ($2/1000 pages). Prices will vary if Advanced or Platinum strategies are applied.",
      },
      {
        value: "ocr_only",
        label: "Platinum",
        description:
          "Platinum/VLM Strategy: $30/1000 pages. For difficult documents, including scanned and handwritten content with VLM API integration.",
      },
    ];

    return (
      <Box
        sx={{
          ...nodeInputStyles,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Checkbox
            label="Basic"
            onChange={(event) =>
              handleParserChange(event, "recursive_character_text_splitter")
            }
            checked={parser?.["recursive_character_text_splitter"]}
          />
          {/* <EditNote
                  onClick={
                    organization?.organization?.subscription === "starter"
                      ? null
                      : handleOpen_RCTS_Dialog
                  }
                  sx={{
                    cursor:
                      organization?.organization?.subscription === "starter"
                        ? "default"
                        : "pointer",
                    opacity:
                      organization?.organization?.subscription === "starter"
                        ? 0.5
                        : 1,
                  }}
                /> */}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Checkbox
            label="Llama Parse"
            onChange={(event) => handleParserChange(event, "llama_parse")}
            checked={parser?.["llama_parse"]}
            disabled={organization?.organization?.subscription === "starter"}
            tooltipText={
              organization?.organization?.subscription === "starter" ? (
                "You need a Pro subscription to access this feature."
              ) : (
                <div>
                  <strong>Best for parsing complex documents:</strong>
                  <ul>
                    <li>
                      Supports file types: <strong>PDFs</strong>,{" "}
                      <strong>DOCX</strong>, and <strong>HTML/URLs</strong>.
                    </li>
                    <li>
                      Handles <strong>tables</strong>, <strong>images</strong>,{" "}
                      <strong>scanned text</strong>, <strong>charts</strong>,
                      and <strong>equations</strong>.
                    </li>
                    <li>
                      <strong>Recommended</strong> for advanced data extraction.
                    </li>
                  </ul>
                  <hr />
                  <p>
                    <strong>Note:</strong> Unsupported file types will be
                    processed using the basic parser.
                  </p>
                </div>
              )
            }
            tooltipPlacement="left"
          />
          <EditNote
            onClick={async () => {
              if (organization?.organization?.subscription === "starter") {
                return null;
              } else {
                handleOpen_LP_Dialog();
                const loadData = async () => {
                  const storedDatasets = await getDataset(`datasets_${nodeId}`);

                  if (storedDatasets) {
                    setDatasets(JSON.parse(storedDatasets));
                  }
                };
                await loadData();
                const __PARSING_INSTRUCTIONS__ = JSON.parse(
                  localStorage.getItem(`parsing_instructions_${nodeId}`)
                );
                if (__PARSING_INSTRUCTIONS__) {
                  setParsing_instructions(__PARSING_INSTRUCTIONS__);
                }
              }
            }}
            sx={{
              cursor:
                organization?.organization?.subscription === "starter"
                  ? "default"
                  : "pointer",
              opacity:
                organization?.organization?.subscription === "starter"
                  ? 0.5
                  : 1,
            }}
            fontSize="small"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Checkbox
            label="Unstructured.io"
            onChange={(event) => handleParserChange(event, "unstructuredio")}
            checked={parser?.["unstructuredio"]}
            disabled={organization?.organization?.subscription === "starter"}
            tooltipText={
              organization?.organization?.subscription === "starter" ? (
                "You need a Pro subscription to access this feature."
              ) : (
                <div>
                  <strong>Best for parsing complex documents:</strong>
                  <ul>
                    <li>
                      Supports file types: <strong>PDFs</strong>,{" "}
                      <strong>DOCX</strong>, and <strong>HTML/URLs</strong>.
                    </li>
                    <li>
                      Handles <strong>tables</strong>, <strong>images</strong>,{" "}
                      <strong>scanned text</strong>, <strong>charts</strong>,
                      and <strong>equations</strong>.
                    </li>
                    <li>
                      <strong>Recommended</strong> for advanced data extraction.
                    </li>
                  </ul>
                  <hr />
                  <p>
                    <strong>Note:</strong> Unsupported file types will be
                    processed using the basic parser.
                  </p>
                </div>
              )
            }
            tooltipPlacement="left"
          />
          <EditNote
            onClick={
              organization?.organization?.subscription === "starter"
                ? null
                : handleOpenUnstructuredIODialog
            }
            fontSize="small"
            sx={{
              cursor:
                organization?.organization?.subscription === "starter"
                  ? "default"
                  : "pointer",
              opacity:
                organization?.organization?.subscription === "starter"
                  ? 0.5
                  : 1,
            }}
          />
        </Box>

        <Dialog
          open={open_RCTS_PromptDialog}
          onClose={handleClose_RCTS_Dialog}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          {/* Dialog Title */}
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitleText}>
              Edit Basic Parser Instructions
            </Typography>
            <ClearIcon
              onClick={handleClose_RCTS_Dialog}
              sx={dialogStyles.dialogCloseIcon}
            />
          </DialogTitle>

          <DialogContent sx={dialogStyles.dialogContent}>
            {Object.keys(splitters).map((type) => (
              <Accordion key={type} sx={accordionStyles.accordionRoot}>
                <AccordionSummary
                  sx={accordionStyles.accordionSummary}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ textTransform: "capitalize", width: "30%" }}
                  >
                    {type} Splitters
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title="Add new splitter" placement="top">
                      <IconButton
                        variant="contained"
                        sx={{
                          backgroundColor: "var(--buttonBackground)",
                          "&:hover": { backgroundColor: "var(--buttonHover)" },
                          width: "15px",
                          height: "15px",
                          marginRight: "20px",
                          color: "var(--bgLight)",
                        }}
                        onClick={() => handleAddSplitter(type)}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={accordionStyles.accordionDetails}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      width: "100%",
                      overflowX: "auto",
                      rowGap: "10px",
                      gap: "10px",
                      "&::-webkit-scrollbar": {
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--buttonBackground)",
                        borderRadius: "10px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },

                      scrollbarWidth: "thin",
                      scrollbarColor: "var(--buttonBackground) transparent",
                    }}
                  >
                    {splitters[type].map((value, index) => (
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#1A1A24",
                          border: "1px solid #555",
                          borderRadius: "5px",
                          padding: "4px",
                          width: "15%",
                          minWidth: "15%",
                        }}
                      >
                        <TextField
                          fullWidth
                          placeholder="Splitter value"
                          inputRef={(el) =>
                            (splitterRefs.current[`${type}-${index}`] = el)
                          }
                          // label={`Splitter ${index + 1}`}
                          value={value}
                          onChange={(e) =>
                            handleInputChangeSplitters(
                              type,
                              index,
                              e.target.value
                            )
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { border: "none" },
                              "& input": { color: "#fff" },
                              "& input::placeholder": {
                                color: "#aaa",
                                opacity: 1,
                              },
                            },
                            "& .MuiInputLabel-root": { color: "#aaa" },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#3498db",
                            },
                          }}
                        />
                        <Tooltip title="Remove splitter" placement="top">
                          <IconButton
                            onClick={() => handleRemoveSplitter(type, index)}
                            sx={{
                              position: "absolute",
                              top: "4px",
                              right: "4px",
                              padding: "2px",
                              width: "17px",
                              height: "17px",
                              color: "var(--bgLight)",
                              fontSize: "15px",
                              backgroundColor: "var(--colorDanger)",
                              "&:hover": {
                                backgroundColor: "var(--colorDangerHover)",
                              }, // Lighter red on hover
                            }}
                          >
                            x
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </DialogContent>
        </Dialog>
        <Dialog
          open={open_LP_PromptDialog}
          onClose={handleClose_LP_Dialog}
          fullWidth={true}
          maxWidth={"md"}
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitleText}>
              Edit Llama Parse Instructions
            </Typography>
            <Clear
              onClick={handleClose_LP_Dialog}
              sx={dialogStyles.dialogCloseIcon}
            />
          </DialogTitle>
          <DialogContent
            sx={{
              ...dialogStyles.dialogContent,
              overflowY: "auto",
              overflowX: "hidden",
              ...scrollbarStyles,
              mt: 2,
            }}
          >
            {/* Accordion wrt dataset names */}
            <Box
              sx={{
                ...dialogStyles.paperSection,
                maxHeight: "none",
                overflowY: "visible",
                height: "auto",
              }}
            >
              <Typography sx={createLLMTypography.fontSizes.sectionTitle}>
                LLama Parse Instructions
              </Typography>
              {datasets?.length === 0 && (
                <Typography sx={dialogStyles.sectionHeader}>
                  No Datasets Available
                </Typography>
              )}

              {datasets?.map((dataset, index) => (
                <Accordion key={index} sx={accordionStyles.accordionRoot}>
                  <AccordionSummary
                    sx={{ ...accordionStyles.accordionSummary, py: 0 }}
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <Typography
                      sx={{
                        fontSize: createLLMTypography.fontSizes.sectionText1,
                      }}
                    >
                      {dataset.fileName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={accordionStyles.accordionDetails}>
                    {/* Mode Selection */}
                    <FormControl fullWidth>
                      <Typography sx={inputStyles.label}>Mode</Typography>
                      <RadioGroup
                        row
                        sx={radioGroupStyles.container}
                        value={
                          parsing_instructions?.[dataset.fileName]?.mode ||
                          "auto"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            dataset.fileName,
                            "mode",
                            e.target.value
                          )
                        }
                      >
                        {[
                          "auto",
                          "premium",
                          "accurate",
                          "Third Party Multimodal",
                          "Layout Agent",
                        ].map((mode) => (
                          <Tooltip key={mode} title={`Mode: ${mode}`}>
                            <FormControlLabel
                              sx={radioGroupStyles.formControlLabel}
                              value={
                                mode === "Third Party Multimodal"
                                  ? "third_party_multimodal"
                                  : mode === "Layout Agent"
                                  ? "parse_page_with_layout_agent"
                                  : mode
                              }
                              control={
                                <Radio
                                  sx={radioGroupStyles.radio}
                                  checkedIcon={
                                    <Circle
                                      fontSize="small"
                                      sx={{ color: theme.main.primaryBlue }}
                                    />
                                  }
                                />
                              }
                              label={
                                mode.charAt(0).toUpperCase() + mode.slice(1)
                              }
                            />
                          </Tooltip>
                        ))}
                      </RadioGroup>
                    </FormControl>

                    {parsing_instructions?.[dataset.fileName]?.mode ===
                      "third_party_multimodal" && (
                      <Box sx={accordionStyles.accordionDetailCards}>
                        <Typography
                          sx={{
                            fontSize:
                              createLLMTypography.fontSizes.sectionText1,
                            mb: 2,
                          }}
                        >
                          Third Party Multimodal
                        </Typography>
                        {/* Vendor Model Select Box */}
                        <FormControl fullWidth variant="outlined">
                          <InputLabel sx={inputStyles.inputLabel}>
                            Vendor Model to Use
                          </InputLabel>
                          <Select
                            value={parsing_instructions[dataset.fileName].llm}
                            onChange={(e) =>
                              handleChangeModelLLM(e, dataset.fileName)
                            }
                            label="Vendor Model to Use"
                            sx={inputStyles.select}
                          >
                            {vendorOptions.map((option, index) => (
                              <MenuItem
                                key={index}
                                value={option.label}
                                sx={{
                                  fontSize:
                                    createLLMTypography.fontSizes.sectionText1,
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    {/* Bounding Box & Title Page Number */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                      }}
                    >
                      <Tooltip title="Specify bounding box margins in the format: top, right, bottom, left (0-1 scale)">
                        <TextField
                          name="bounding_box"
                          label="Bounding Box"
                          placeholder="0.1,0.2,0,0.5"
                          value={
                            parsing_instructions?.[dataset.fileName]
                              ?.bounding_box || "0,0,0,0"
                          }
                          onChange={(e) =>
                            handleInputChange(
                              dataset.fileName,
                              "bounding_box",
                              e.target.value
                            )
                          }
                          fullWidth
                          sx={inputStyles.textField}
                        />
                      </Tooltip>

                      <Tooltip title="Specify the title page number (default: 1)">
                        <TextField
                          name="title_page_number"
                          label="Title Page Number"
                          placeholder="1"
                          defaultValue={"1"}
                          value={
                            parsing_instructions?.[dataset.fileName]
                              ?.title_page_number
                          }
                          onChange={(e) =>
                            handleInputChange(
                              dataset.fileName,
                              "title_page_number",
                              e.target.value
                            )
                          }
                          fullWidth
                          sx={inputStyles.textField}
                        />
                      </Tooltip>
                    </Box>

                    {/* Exclude Title Page Checkbox */}
                    <Tooltip title="Exclude the title page from parsing">
                      <FormControl sx={{ px: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                parsing_instructions?.[dataset.fileName]
                                  ?.exclude_title_page || false
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  dataset.fileName,
                                  "exclude_title_page",
                                  e.target.checked
                                )
                              }
                              sx={inputStyles.checkbox.control}
                            />
                          }
                          label="Exclude Title Page"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            "& .MuiFormControlLabel-label": {
                              fontSize:
                                createLLMTypography.fontSizes.sectionText1,
                              color: theme.inputs.textColor,
                            },
                          }}
                        />
                      </FormControl>
                    </Tooltip>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
            {/* End Accordion wrt dataset names */}

            <Box
              sx={{
                ...dialogStyles.paperSection,
                maxHeight: "none",
                overflowY: "visible",
                height: "auto",
              }}
            >
              <Typography sx={createLLMTypography.fontSizes.sectionTitle}>
                Default LLama Parse Instructions
              </Typography>
              {datasets?.length === 0 ? (
                <Typography sx={dialogStyles.sectionHeader}>
                  No Datasets Available
                </Typography>
              ) : (
                <>
                  {/* Mode Selection */}
                  <FormControl fullWidth>
                    <Typography sx={inputStyles.label}>Mode</Typography>
                    <RadioGroup
                      row
                      value={global_parsing_instructions?.mode || "auto"}
                      onChange={(e) =>
                        handleInputChange("All", "mode", e.target.value)
                      }
                      sx={radioGroupStyles.container}
                    >
                      {[
                        "auto",
                        "premium",
                        "accurate",
                        "Third Party Multimodal",
                        "Layout Agent",
                      ].map((mode) => (
                        <Tooltip key={mode} title={`Mode: ${mode}`}>
                          <FormControlLabel
                            sx={radioGroupStyles.formControlLabel}
                            value={
                              mode === "Third Party Multimodal"
                                ? "third_party_multimodal"
                                : mode === "Layout Agent"
                                ? "parse_page_with_layout_agent"
                                : mode
                            }
                            control={
                              <Radio
                                sx={radioGroupStyles.radio}
                                checkedIcon={
                                  <Circle
                                    fontSize="small"
                                    sx={{ color: theme.main.primaryBlue }}
                                  />
                                }
                              />
                            }
                            label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                          />
                        </Tooltip>
                      ))}
                    </RadioGroup>
                  </FormControl>

                  {global_parsing_instructions?.mode ===
                    "third_party_multimodal" && (
                    <Box sx={accordionStyles.accordionDetailCards}>
                      <Typography
                        sx={{
                          fontSize: createLLMTypography.fontSizes.sectionText1,
                          mb: 2,
                        }}
                      >
                        Third Party Multimodal
                      </Typography>

                      <FormControl fullWidth>
                        <InputLabel sx={inputStyles.inputLabel}>
                          Vendor Model to Use
                        </InputLabel>
                        <Select
                          value={global_parsing_instructions.llm}
                          onChange={(e) => handleChangeModelLLM(e, "All")}
                          label="Vendor Model to Use"
                          sx={inputStyles.select}
                        >
                          {vendorOptions.map((option, index) => (
                            <MenuItem
                              key={index}
                              value={option.label}
                              sx={{
                                fontSize:
                                  createLLMTypography.fontSizes.sectionText1,
                              }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  {/* Bounding Box & Title Page Number */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    }}
                  >
                    <Tooltip title="Specify bounding box margins in the format: top, right, bottom, left (0-1 scale)">
                      <TextField
                        name="bounding_box"
                        label="Bounding Box"
                        placeholder="0.1,0.2,0,0.5"
                        value={
                          global_parsing_instructions?.bounding_box || "0,0,0,0"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "All",
                            "bounding_box",
                            e.target.value
                          )
                        }
                        fullWidth
                        sx={inputStyles.textField}
                      />
                    </Tooltip>

                    <Tooltip title="Specify the title page number (default: 1)">
                      <TextField
                        name="title_page_number"
                        label="Title Page Number"
                        placeholder="1"
                        defaultValue={"1"}
                        value={global_parsing_instructions?.title_page_number}
                        onChange={(e) =>
                          handleInputChange(
                            "All",
                            "title_page_number",
                            e.target.value
                          )
                        }
                        fullWidth
                        sx={inputStyles.textField}
                      />
                    </Tooltip>
                  </Box>

                  {/* Exclude Title Page Checkbox */}
                  <Tooltip title="Exclude the title page from parsing">
                    <FormControl sx={{ px: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              global_parsing_instructions?.exclude_title_page ||
                              false
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "All",
                                "exclude_title_page",
                                e.target.checked
                              )
                            }
                            sx={inputStyles.checkbox.control}
                          />
                        }
                        label="Exclude Title Page"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          "& .MuiFormControlLabel-label": {
                            fontSize:
                              createLLMTypography.fontSizes.sectionText1,
                            color: theme.inputs.textColor,
                          },
                        }}
                      />
                    </FormControl>
                  </Tooltip>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      width: "100%",
                      py: "8px",
                    }}
                  >
                    <Tooltip title="Apply the same settings to all datasets">
                      <Button
                        sx={primaryButtonStyles}
                        onClick={handleApplyForAll}
                      >
                        Apply to all
                      </Button>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Box>

            {/* End Accordion wrt global */}

            <Box
              sx={{
                ...dialogStyles.paperSection,
                maxHeight: "none",
                overflowY: "visible",
                height: "auto",
              }}
            >
              <Typography sx={inputStyles.label}>Instructions</Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={llamaParsePrompt}
                onChange={(e) => setLlamaParsePrompt(e.target.value)}
                placeholder="Edit the prompt here"
                sx={inputStyles.textField}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  onClick={handleReset_LP_Dialog}
                  sx={tertiaryButtonStyles}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleSave_LP_Dialog}
                  sx={primaryButtonStyles}
                  disabled={
                    llamaParsePrompt?.trim() === "" ||
                    (llamaParsePrompt === originalPrompt &&
                      llamaParseModeStr === originalModeStr)
                  }
                >
                  Save
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
        <Dialog
          open={openUnstructuredIODialog}
          onClose={handleCloseUnstructuredIODialog}
          fullWidth={true}
          maxWidth={"sm"}
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            <Typography sx={dialogStyles.dialogTitle}>
              Edit Unstructured.io Mode
            </Typography>
            <Clear
              onClick={handleCloseUnstructuredIODialog}
              sx={dialogStyles.dialogCloseIcon}
            />
          </DialogTitle>
          <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
            <FormControl>
              <RadioGroup
                value={Object.keys(unstructuredIOMode).find(
                  (k) => unstructuredIOMode[k]
                )}
                onChange={handleUnstructuredIOModeChange}
                sx={radioGroupStyles.container}
              >
                {ioModes.map(({ value, label, description }) => (
                  <Tooltip
                    key={value}
                    title={description}
                    placement="top-start"
                  >
                    <FormControlLabel
                      sx={radioGroupStyles.formControlLabel}
                      value={value}
                      control={
                        <Radio
                          sx={radioGroupStyles.radio}
                          checkedIcon={
                            <Circle
                              fontSize="small"
                              sx={{ color: theme.main.primaryBlue }}
                            />
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: theme.typography.fontSize.sm,
                              color: theme.inputs.textColor,
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              display: "block",
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.textColors.dark.secondary,
                              lineHeight: 1.2,
                            }}
                          >
                            {description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions sx={dialogStyles.dialogActions}>
            <Button
              onClick={handleResetUnstructuredIODialog}
              sx={tertiaryButtonStyles}
            >
              Reset
            </Button>
            <Button
              onClick={handleSaveUnstructuredIODialog}
              sx={primaryButtonStyles}
              disabled={
                JSON.stringify(unstructuredIOMode) ===
                JSON.stringify(originalUnstructuredIOModeState)
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

export default DocumentParser;
